import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Trans } from 'react-i18next';
import { startOfMinute } from 'date-fns';
import { Typography } from '@components/Typography';
import { InternalLink } from '@components/Link';
import { useTranslation } from '@hooks';
import { ConversationStatus } from '@constants/conversations';
import { InboxHistory, MessageStatus } from '@constants/inbox';
import { CHANNEL, STATUS } from '@constants/channels';
import { RoutePath } from '@constants/routes';
import { InboxEmptyIcon } from 'assets/images/icons';
import Message from './Message';
import ConversationsEvent from './ConversationsEvent';
import InboxInput from './InboxInput';
import InboxHeader from './InboxHeader';
import SendWhatsAppTemplate from './SendWhatsAppTemplate';
import { Container, Header, Feed, Footer } from './styles';

const Inbox = ({ isMobile, filterRef, activeConversation, setActiveConversation, setContactOpened }) => {
    const [whatsAppTemplateModalOpen, setWhatsAppTemplateModalOpen] = useState(false);

    const { data: messages, loading } = useSelector(state => state.inbox);
    const account = useSelector(state => state.account?.account);
    const users = useSelector(state => state.account?.account?.users || []);
    const agents = useSelector(state => state.agents?.data || []);
    const locations = useSelector(state => state.account?.account?.companies || []);

    const { t } = useTranslation();

    const isConversationOpen = activeConversation?.status === ConversationStatus.Open;

    const messagesHistory = useMemo(() => {
        const sortedMessages = [...messages].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const getNormalizedDate = dateStr => startOfMinute(new Date(dateStr)).getTime();

        const createMessageObject = msg => ({
            content: msg.content,
            contentType: msg.contentType,
            createdAt: msg.createdAt,
            replyMessage: msg.replyMessage,
            senderType: msg.senderType,
            status: msg.status,
        });

        return sortedMessages.reduce((groups, message) => {
            const lastGroup = groups[groups.length - 1];
            const messageTime = getNormalizedDate(message.createdAt);

            const shouldCreateNewGroup =
                !lastGroup ||
                messageTime !== getNormalizedDate(lastGroup.createdAt) ||
                message.type !== lastGroup.type ||
                message.modelType !== lastGroup.modelType ||
                message.status === MessageStatus.Failed;

            if (shouldCreateNewGroup) {
                groups.push({
                    ...message,
                    children: [createMessageObject(message)],
                    createdAt: new Date(messageTime),
                });
            } else {
                lastGroup.children.push(createMessageObject(message));
                lastGroup.children.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            }

            return groups;
        }, []);
    }, [messages]);

    const isArchived = activeConversation?.channelId === CHANNEL.ARCHIVED;
    const isWhatsAppInactive = activeConversation?.messagingWindowClosed;

    return (
        <Container hasConversation={!!activeConversation}>
            {activeConversation && (
                <>
                    <Header>
                        <InboxHeader
                            isMobile={isMobile}
                            setContactOpened={setContactOpened}
                            activeConversation={activeConversation}
                            setActiveConversation={setActiveConversation}
                            locations={locations}
                            filterRef={filterRef}
                        />
                    </Header>
                    <Feed>
                        {!loading &&
                            messagesHistory.map((item, index) =>
                                item.modelType === InboxHistory.Message ? (
                                    <Message
                                        key={index}
                                        data={item}
                                        users={users}
                                        locations={locations}
                                        assignedLocation={
                                            activeConversation?.locations?.length === 1
                                                ? activeConversation?.locations[0]
                                                : null
                                        }
                                        channel={activeConversation?.channelType}
                                        channelStatus={activeConversation?.channelStatus}
                                    />
                                ) : (
                                    <ConversationsEvent key={index} data={item} users={users} agents={agents} />
                                ),
                            )}
                    </Feed>
                    <Footer>
                        {isArchived ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}
                            >
                                {t('InboxPage.archivedConversation')}
                            </Typography>
                        ) : activeConversation?.channelStatus === STATUS.DISCONNECTED ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}
                            >
                                <Trans t={t} i18nKey="InboxPage.disconnectedChannel">
                                    <InternalLink
                                        to={RoutePath.AccountSettingsChannels}
                                        variant="caption"
                                        align="center"
                                        underline="hover"
                                    >
                                        {{ link: t('InboxPage.settings') }}
                                    </InternalLink>
                                </Trans>
                            </Typography>
                        ) : !isConversationOpen ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}
                            >
                                {t('InboxPage.closedConversation')}
                            </Typography>
                        ) : activeConversation?.assignedAgentId ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}
                            >
                                {t('InboxPage.conversationAssignedOnPulse')}
                            </Typography>
                        ) : isWhatsAppInactive ? (
                            <Typography
                                variant="caption"
                                sx={{ textAlign: 'center', display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}
                            >
                                <Trans t={t} i18nKey="InboxPage.whatsApp24HoursWindow">
                                    <Typography
                                        onClick={() => setWhatsAppTemplateModalOpen(true)}
                                        variant="caption"
                                        sx={{
                                            color: '#1F4C5C',
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' },
                                        }}
                                    >
                                        {{ link: t('InboxPage.useApprovedMessage') }}
                                    </Typography>
                                </Trans>
                            </Typography>
                        ) : (
                            <InboxInput
                                activeConversation={activeConversation}
                                surveysEnabled={account?.surveysEnabled}
                                couponsEnabled={account?.couponsEnabled}
                            />
                        )}
                    </Footer>
                </>
            )}
            {!activeConversation && (
                <div
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                >
                    <InboxEmptyIcon />
                    <Typography variant="body1" sx={{ textAlign: 'center', marginTop: '16px' }}>
                        {t('InboxPage.noConversationsSelected')}
                    </Typography>
                </div>
            )}
            {whatsAppTemplateModalOpen ? (
                <SendWhatsAppTemplate
                    isOpen={whatsAppTemplateModalOpen}
                    onClose={() => setWhatsAppTemplateModalOpen(false)}
                    channelId={activeConversation?.channelId}
                    conversationId={activeConversation?.id}
                />
            ) : null}
        </Container>
    );
};

export default Inbox;
